// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <https://benrichardson.dev>
// Based on QuakeWatch by Ben Richardson — https://benrichardson.dev
import { rankRows, svgMatrix } from '../components/charts';
import { openRegionDrawer } from '../components/detail';
import { gterm, infoI } from '../components/overlays';
import { joules, mag as fmtMag, num } from '../format';
import { groupBy, magColor, type Group } from '../model';
import { quakes, regionOf } from '../state';
import { emptyState } from './shared';

type Metric = 'count' | 'energy';
let metric: Metric = 'count';

const MAG_BANDS: Array<{ label: string; lo: number; hi: number }> = [
  { label: '<2', lo: -Infinity, hi: 2 },
  { label: '2–3', lo: 2, hi: 3 },
  { label: '3–4', lo: 3, hi: 4 },
  { label: '4–5', lo: 4, hi: 5 },
  { label: '5–6', lo: 5, hi: 6 },
  { label: '6+', lo: 6, hi: Infinity },
];

export function renderRegions(root: HTMLElement): void {
  const list = quakes();
  if (list.length === 0) { root.innerHTML = emptyState(); return; }

  const groups = groupBy(list, regionOf);
  const ranked = [...groups].sort((a, b) => (metric === 'count' ? b.count - a.count : b.energy - a.energy));
  const top = ranked.slice(0, 14);
  const maxVal = Math.max(...top.map((g) => (metric === 'count' ? g.count : g.energy)), 1);
  const rows = rankRows(top.map((g) => ({
    label: g.key,
    frac: (metric === 'count' ? g.count : g.energy) / maxVal,
    valueLabel: metric === 'count' ? `${g.count}` : joules(g.energy),
    color: magColor(g.maxMag),
    tip: `${g.key}\n${g.count} events · largest M${fmtMag(g.maxMag)} · ${joules(g.energy)}`,
    id: g.key,
    sub: `max M${fmtMag(g.maxMag)}`,
  })));

  // matrix: top regions × magnitude bands
  const matRegions = ranked.slice(0, 10);
  const cellCounts: number[][] = matRegions.map((g) =>
    MAG_BANDS.map((band) => g.quakes.filter((q) => q.mag >= band.lo && q.mag < band.hi).length),
  );
  const maxCell = Math.max(1, ...cellCounts.flat());

  root.innerHTML = `
    <div class="between">
      <div>
        <div class="panel-title">Most active regions ${infoI('tectonic_plate')}</div>
        <div class="panel-sub" style="margin:0">Every earthquake is classified to the ${gterm('tectonic_plate', 'tectonic plate')} it sits on. Click a region to see its biggest events.</div>
      </div>
      <div class="segmented" role="tablist" aria-label="Metric">
        <button class="${metric === 'count' ? 'active' : ''}" data-metric="count">By count</button>
        <button class="${metric === 'energy' ? 'active' : ''}" data-metric="energy">By energy</button>
      </div>
    </div>
    <div class="panel" style="margin-top:0.75rem">${rows}</div>

    <div class="panel" style="margin-top:1rem">
      <div class="panel-title">Region × magnitude</div>
      <div class="panel-sub">Where the activity sits by size. A region can rack up many small tremors, or a few large ones — the cells tell them apart. Darker = more events.</div>
      <div class="chart-wrap">${svgMatrix({
        rowLabels: matRegions.map((g) => g.key),
        colLabels: MAG_BANDS.map((b) => b.label),
        width: 820,
        cell: (r, c) => {
          const v = cellCounts[r][c];
          return { value: v, color: heat(v, maxCell), tip: `${matRegions[r].key} · M ${MAG_BANDS[c].label}: ${v} event${v === 1 ? '' : 's'}` };
        },
      })}</div>
      <div class="legend"><span>${num(matRegions.length)} regions · ${MAG_BANDS.length} magnitude bands</span></div>
    </div>`;

  root.querySelectorAll('[data-metric]').forEach((b) => b.addEventListener('click', () => {
    metric = b.getAttribute('data-metric') as Metric;
    renderRegions(root);
  }));
  root.querySelectorAll('.rank-row[data-id]').forEach((r) => r.addEventListener('click', () => {
    const g = groups.find((x: Group) => x.key === r.getAttribute('data-id'));
    if (g) openRegionDrawer(g.key, g.quakes);
  }));
}

function heat(v: number, max: number): string {
  if (v <= 0) return '#0e1a2e';
  const t = Math.min(1, Math.sqrt(v / max));
  // dark blue → cyan → amber
  const r = Math.round(14 + t * (250 - 14));
  const g = Math.round(26 + t * (200 - 26));
  const b = Math.round(46 + t * (90 - 46));
  return `rgb(${r},${g},${b})`;
}
